/**
 * Questionnaire Wizard - אשף שאלון סיכון
 * Mobile-first single-screen layout, no scrolling needed
 */

import { useState, useEffect } from 'react'
import { ChevronRight, ChevronLeft } from 'lucide-react'
import type { Question, QuestionResponse } from '../../types'

interface WizardQuestion extends Question {
  sectionIcon: string
  showSectionHeader: boolean
  isKeyRiskQuestion: boolean
}

const questions: WizardQuestion[] = [
  // ===== Section א: מצב כלכלי והעדפות (Q1-Q8) =====
  {
    _id: '1',
    questionText: 'מהי ההכנסה הכוללת החודשית למשק הבית?',
    questionType: 'single_choice',
    category: 'financial_situation',
    section: 'מצב כלכלי והעדפות',
    sectionIcon: '💰',
    showSectionHeader: true,
    isKeyRiskQuestion: false,
    options: [
      { value: 1, label: 'עד 5,000 ₪', score: 1 },
      { value: 2, label: 'בין 5,000 ₪ עד 10,000 ₪', score: 2 },
      { value: 3, label: 'בין 10,000 ₪ ל-40,000 ₪', score: 3 },
      { value: 4, label: 'בין 40,000 ₪ ל-100,000 ₪', score: 4 },
      { value: 5, label: 'מעל 100,000 ₪', score: 5 },
    ],
    weight: 1.0, orderIndex: 1, isRequired: true,
  },
  {
    _id: '2',
    questionText: 'מהו השווי המוערך של כלל החסכונות והנכסים שלך?',
    questionType: 'single_choice',
    category: 'financial_situation',
    section: 'מצב כלכלי והעדפות',
    sectionIcon: '💰',
    showSectionHeader: false,
    isKeyRiskQuestion: false,
    options: [
      { value: 1, label: 'אין לי נכסים ו/או חסכונות כלשהם', score: 1 },
      { value: 2, label: 'עד 100,000 ₪', score: 2 },
      { value: 3, label: 'בין 100,000 ₪ ל-400,000 ₪', score: 3 },
      { value: 4, label: 'בין 400,000 ₪ ל-1,000,000 ₪', score: 4 },
      { value: 5, label: 'מעל 1,000,000 ₪', score: 5 },
    ],
    weight: 1.0, orderIndex: 2, isRequired: true,
  },
  {
    _id: '3',
    questionText: 'מהו השווי המוערך של כלל ההתחייבויות וההוצאות שלך?',
    questionType: 'single_choice',
    category: 'financial_situation',
    section: 'מצב כלכלי והעדפות',
    sectionIcon: '💰',
    showSectionHeader: false,
    isKeyRiskQuestion: false,
    options: [
      { value: 1, label: 'אין לי התחייבויות', score: 5 },
      { value: 2, label: 'עד 100,000 ₪', score: 4 },
      { value: 3, label: 'בין 100,000 ₪ ל-400,000 ₪', score: 3 },
      { value: 4, label: 'בין 400,000 ₪ ל-1,000,000 ₪', score: 2 },
      { value: 5, label: 'מעל 1,000,000 ₪', score: 1 },
    ],
    weight: 1.0, orderIndex: 3, isRequired: true,
  },
  {
    _id: '4',
    questionText: 'האם יש לך יותר נכסים מהתחייבויות או להיפך? אנא פרט:',
    questionType: 'text',
    category: 'financial_situation',
    section: 'מצב כלכלי והעדפות',
    sectionIcon: '💰',
    showSectionHeader: false,
    isKeyRiskQuestion: false,
    options: [],
    weight: 0, orderIndex: 4, isRequired: false,
  },
  {
    _id: '5',
    questionText: 'האם יש לך הוצאות חד פעמיות צפויות?',
    questionType: 'single_choice',
    category: 'financial_situation',
    section: 'מצב כלכלי והעדפות',
    sectionIcon: '💰',
    showSectionHeader: false,
    isKeyRiskQuestion: false,
    options: [
      { value: 1, label: 'לא', score: 5 },
      { value: 2, label: 'כן, עד 15,000 ₪', score: 4 },
      { value: 3, label: 'כן, בין 15,000 ₪ ל-50,000 ₪', score: 3 },
      { value: 4, label: 'כן, בין 50,000 ₪ ל-100,000 ₪', score: 2 },
      { value: 5, label: 'כן, מעל 100,000 ₪', score: 1 },
    ],
    weight: 1.0, orderIndex: 5, isRequired: true,
  },
  {
    _id: '6',
    questionText: 'מהו השיעור שמהווה הסכום להשקעה מסך הנכסים הפיננסיים שלך?',
    questionType: 'single_choice',
    category: 'financial_situation',
    section: 'מצב כלכלי והעדפות',
    sectionIcon: '💰',
    showSectionHeader: false,
    isKeyRiskQuestion: false,
    options: [
      { value: 1, label: 'פחות מ-15%', score: 1 },
      { value: 2, label: 'בין 15%-40%', score: 2 },
      { value: 3, label: 'בין 40%-70%', score: 3 },
      { value: 4, label: 'יותר מ-70%', score: 4 },
    ],
    weight: 1.0, orderIndex: 6, isRequired: true,
  },
  {
    _id: '7',
    questionText: 'תקופת ההשקעה הצפויה',
    questionType: 'single_choice',
    category: 'financial_situation',
    section: 'מצב כלכלי והעדפות',
    sectionIcon: '💰',
    showSectionHeader: false,
    isKeyRiskQuestion: true,
    options: [
      { value: 1, label: 'עד שנתיים', score: 1 },
      { value: 2, label: 'בין שנתיים לחמש שנים', score: 3 },
      { value: 3, label: 'מעל חמש שנים', score: 5 },
    ],
    weight: 1.0, orderIndex: 7, isRequired: true,
  },
  {
    _id: '8',
    questionText: 'מהי המטרה העיקרית של ההשקעה שלך?',
    questionType: 'single_choice',
    category: 'financial_situation',
    section: 'מצב כלכלי והעדפות',
    sectionIcon: '💰',
    showSectionHeader: false,
    isKeyRiskQuestion: false,
    options: [
      { value: 1, label: 'לחסוך לצורך רכישה עתידית גדולה', score: 2 },
      { value: 2, label: 'לחסוך לגיל פרישה', score: 4 },
      { value: 3, label: 'לחסוך למען הבטחת עתיד משפחתי/ילדיי', score: 3 },
      { value: 4, label: 'אחר', score: 3, requiresText: true },
    ],
    weight: 1.0, orderIndex: 8, isRequired: true,
  },

  // ===== Section ב: ניסיון בשוק ההון (Q9-Q10) =====
  {
    _id: '9',
    questionText: 'מה הניסיון ו/או הידע המקצועי שלך בשוק ההון?',
    questionType: 'single_choice',
    category: 'knowledge',
    section: 'ניסיון בשוק ההון',
    sectionIcon: '📈',
    showSectionHeader: true,
    isKeyRiskQuestion: true,
    options: [
      { value: 1, label: 'אין לי ניסיון ו/או ידע מקצועי', score: 1 },
      { value: 2, label: 'יש לי ניסיון ו/או ידע בסיסי', score: 2 },
      { value: 3, label: 'יש לי ניסיון ו/או ידע ברמה בינונית', score: 3 },
      { value: 4, label: 'בעל ניסיון וידע רחבים שנצברו במהלך השנים', score: 4 },
    ],
    weight: 1.0, orderIndex: 9, isRequired: true,
  },
  {
    _id: '10',
    questionText: 'מהי מידת ההיכרות שלך עם מכשירים פיננסיים כדוגמת תעודות סל וקרנות נאמנות?',
    questionType: 'single_choice',
    category: 'knowledge',
    section: 'ניסיון בשוק ההון',
    sectionIcon: '📈',
    showSectionHeader: false,
    isKeyRiskQuestion: false,
    options: [
      { value: 1, label: 'אין לי היכרות ו/או ניסיון במכשירים פיננסיים', score: 1 },
      { value: 2, label: 'אני מכיר את מהות ההשקעה אולם אין לי ניסיון', score: 2 },
      { value: 3, label: 'יש לי ניסיון מסוים בהשקעה באמצעות מכשירים אלה', score: 3 },
      { value: 4, label: 'יש לי ניסיון רב בהשקעה באמצעות מכשירים אלה', score: 4 },
    ],
    weight: 1.0, orderIndex: 10, isRequired: true,
  },

  // ===== Section ג: יחס לסיכון (Q11-Q12) =====
  {
    _id: '11',
    questionText: 'מה מידת הסבלנות שלך כלפי סיכון?',
    questionType: 'single_choice',
    category: 'risk_tolerance',
    section: 'יחס לסיכון',
    sectionIcon: '⚖️',
    showSectionHeader: true,
    isKeyRiskQuestion: true,
    options: [
      { value: 1, label: 'נמוכה מאוד — לא מוכן להקצות הון להשקעות בסיכון גבוה', score: 1 },
      { value: 2, label: 'נמוכה — מוכן להקצות חלק קטן מהממון לסיכון גבוה', score: 2 },
      { value: 3, label: 'בינונית — מוכן להקצות חלק מסוים מהממון לסיכון גבוה', score: 3 },
      { value: 4, label: 'גבוהה — מוכן להקצות חלק גדול מהממון לסיכון גבוה', score: 4 },
    ],
    weight: 1.0, orderIndex: 11, isRequired: true,
  },
  {
    _id: '12',
    questionText: 'כיצד הנך צפוי להגיב להפסד של 15% בהשקעתך?',
    questionType: 'single_choice',
    category: 'risk_tolerance',
    section: 'יחס לסיכון',
    sectionIcon: '⚖️',
    showSectionHeader: false,
    isKeyRiskQuestion: true,
    options: [
      { value: 1, label: 'אמכור מיד הכל למניעת הפסד גדול יותר', score: 1 },
      { value: 2, label: 'אהיה מוטרד מהפסד זה', score: 2 },
      { value: 3, label: 'צופה תנודתיות לטווח קצר, אתעדכן שהפסד לא גדל', score: 3 },
      { value: 4, label: 'צופה תנודתיות גבוהה וארוכת טווח, אהיה רגוע', score: 4 },
    ],
    weight: 1.0, orderIndex: 12, isRequired: true,
  },
]

function mapSumToLevel(sum: number): number {
  if (sum <= 6) return 1
  if (sum <= 9) return 2
  if (sum <= 12) return 3
  if (sum <= 15) return 4
  return 5
}

function mapLevelToNormalizedScore(level: number): number {
  const mapping: Record<number, number> = { 1: 2, 2: 4, 3: 6, 4: 8, 5: 10 }
  return mapping[level] ?? 2
}

function getRiskProfileLabel(normalizedScore: number): string {
  if (normalizedScore <= 2) return 'סיכון נמוך'
  if (normalizedScore <= 4) return 'סיכון נמוך-בינוני'
  if (normalizedScore <= 6) return 'סיכון בינוני'
  if (normalizedScore <= 8) return 'סיכון בינוני-גבוה'
  return 'סיכון גבוה'
}

interface QuestionnaireWizardProps {
  onComplete: (result: {
    responses: QuestionResponse[]
    riskScore: number
    riskProfile: string
  }) => void
}

export default function QuestionnaireWizard({ onComplete }: QuestionnaireWizardProps) {
  const [currentStep, setCurrentStep] = useState(1)
  const [answers, setAnswers] = useState<Record<string, number>>({})
  const [textAnswers, setTextAnswers] = useState<Record<string, string>>({})
  const [error, setError] = useState<string | null>(null)

  const totalSteps = questions.length
  const currentQuestion = questions[currentStep - 1]
  const isTextQuestion = currentQuestion.questionType === 'text'
  const selectedValue = answers[currentQuestion._id]
  const progress = Math.round((currentStep / totalSteps) * 100)

  useEffect(() => {
    const saved = localStorage.getItem('questionnaire-progress')
    if (saved) {
      try {
        const { answers: a, textAnswers: t, currentStep: s } = JSON.parse(saved)
        setAnswers(a || {})
        setTextAnswers(t || {})
        setCurrentStep(s)
      } catch {
        localStorage.removeItem('questionnaire-progress')
      }
    }
  }, [])

  const saveProgress = (a: Record<string, number>, t: Record<string, string>) => {
    localStorage.setItem('questionnaire-progress', JSON.stringify({ answers: a, textAnswers: t, currentStep }))
  }

  const handleAnswer = (value: number) => {
    const updated = { ...answers, [currentQuestion._id]: value }
    setAnswers(updated)
    setError(null)
    saveProgress(updated, textAnswers)
  }

  const handleTextAnswer = (id: string, text: string) => {
    const updated = { ...textAnswers, [id]: text }
    setTextAnswers(updated)
    saveProgress(answers, updated)
  }

  const handleNext = () => {
    if (!isTextQuestion && !answers[currentQuestion._id]) {
      setError('נא לבחור תשובה לפני המשך')
      return
    }
    if (currentStep < totalSteps) {
      setCurrentStep((p) => p + 1)
      setError(null)
    } else {
      calculateAndSubmit()
    }
  }

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep((p) => p - 1)
      setError(null)
    }
  }

  const calculateAndSubmit = () => {
    const keyIds = ['7', '9', '11', '12']
    const sum = keyIds.reduce((acc, qId) => {
      const q = questions.find((q) => q._id === qId)!
      const opt = q.options.find((o) => o.value === answers[qId])
      return acc + (opt?.score ?? 1)
    }, 0)
    const level = mapSumToLevel(sum)
    const normalizedScore = mapLevelToNormalizedScore(level)
    const riskProfile = getRiskProfileLabel(normalizedScore)

    const responses: QuestionResponse[] = questions
      .filter((q) => q._id !== '4' && q.questionType !== 'text')
      .map((q) => {
        const val = answers[q._id]
        const opt = q.options.find((o) => o.value === val)
        return { questionId: q._id, answer: val ?? 1, score: opt?.score ?? 1 }
      })

    localStorage.removeItem('questionnaire-progress')
    onComplete({ responses, riskScore: normalizedScore, riskProfile })
  }

  return (
    <div className="flex flex-col gap-3" dir="rtl">

      {/* Progress bar + step indicator */}
      <div>
        <div className="flex items-center justify-between mb-1">
          <div className="flex items-center gap-1.5 min-w-0">
            <span className="text-sm">{currentQuestion.sectionIcon}</span>
            <span className="text-xs text-gray-500 truncate">{currentQuestion.section}</span>
          </div>
          <span className="text-xs font-semibold text-primary-600 whitespace-nowrap mr-2">
            {currentStep} / {totalSteps}
          </span>
        </div>
        <div
          className="w-full bg-gray-200 rounded-full h-1.5"
          role="progressbar"
          aria-valuenow={currentStep}
          aria-valuemin={1}
          aria-valuemax={totalSteps}
          aria-label={`שאלה ${currentStep} מתוך ${totalSteps}`}
        >
          <div
            className="bg-primary-600 h-1.5 rounded-full transition-all duration-300"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      {/* Question card */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
        <p id={`wizard-question-${currentQuestion._id}`} className="text-base font-semibold text-gray-900 mb-3 leading-snug">
          {currentQuestion.questionText}
        </p>

        {/* Text question */}
        {isTextQuestion ? (
          <textarea
            className="w-full border border-gray-300 rounded-lg p-3 text-base text-gray-900 resize-none focus:outline-none focus:ring-2 focus:ring-primary-500"
            rows={3}
            dir="rtl"
            placeholder="הזן את תשובתך כאן (אופציונלי)..."
            value={textAnswers[currentQuestion._id] ?? ''}
            onChange={(e) => handleTextAnswer(currentQuestion._id, e.target.value)}
            aria-labelledby={`wizard-question-${currentQuestion._id}`}
          />
        ) : (
          <div className="space-y-2" role="radiogroup" aria-labelledby={`wizard-question-${currentQuestion._id}`}>
            {currentQuestion.options.map((option) => (
              <div key={option.value}>
                <button
                  type="button"
                  role="radio"
                  aria-checked={selectedValue === option.value}
                  onClick={() => handleAnswer(option.value)}
                  className={`w-full text-right px-3 py-2.5 rounded-lg border-2 transition-all duration-150 ${
                    selectedValue === option.value
                      ? 'border-primary-600 bg-primary-50'
                      : 'border-gray-200 bg-white active:bg-gray-50'
                  }`}
                >
                  <div className="flex items-center gap-2.5">
                    <div
                      className={`flex-shrink-0 w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                        selectedValue === option.value
                          ? 'border-primary-600 bg-primary-600'
                          : 'border-gray-300 bg-white'
                      }`}
                    >
                      {selectedValue === option.value && (
                        <div className="w-1.5 h-1.5 bg-white rounded-full" />
                      )}
                    </div>
                    <span className="text-sm font-medium text-gray-900 leading-snug">
                      {option.label}
                    </span>
                  </div>
                </button>

                {option.requiresText && selectedValue === option.value && (
                  <div className="mt-1.5 mr-6">
                    <input
                      type="text"
                      dir="rtl"
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-base focus:outline-none focus:ring-2 focus:ring-primary-500"
                      placeholder="אנא פרט..."
                      value={textAnswers[currentQuestion._id] ?? ''}
                      onChange={(e) => handleTextAnswer(currentQuestion._id, e.target.value)}
                      aria-label="פירוט נוסף"
                    />
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Error */}
      {error && (
        <p className="text-red-600 text-sm text-center -mt-1" role="alert">{error}</p>
      )}

      {/* Navigation buttons */}
      <div className="flex gap-3">
        <button
          type="button"
          onClick={handleBack}
          disabled={currentStep === 1}
          className="flex-1 py-2.5 rounded-xl border-2 border-gray-300 text-gray-700 font-medium text-sm disabled:opacity-40 flex items-center justify-center gap-1 transition-colors active:bg-gray-50"
        >
          <ChevronRight size={17} aria-hidden="true" />
          חזור
        </button>
        <button
          type="button"
          onClick={handleNext}
          className="flex-1 py-2.5 rounded-xl bg-primary-600 text-white font-semibold text-sm flex items-center justify-center gap-1 transition-colors active:bg-primary-700"
        >
          {currentStep === totalSteps ? 'סיים שאלון' : 'הבא'}
          {currentStep < totalSteps && <ChevronLeft size={17} aria-hidden="true" />}
        </button>
      </div>
    </div>
  )
}
